import React, { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phonenumber: '',
    projectType: '',
    message: '',
    subscribe: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim() || !formData.email.includes('@')) {
      newErrors.email = 'Valid email is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const response = await fetch('https://formspree.io/f/xrbknpzw', {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        body: new FormData(e.target)
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json();
        if (data.errors) {
          setErrors({ submit: data.errors.map(error => error.message).join(', ') });
        } else {
          setErrors({ submit: 'Oops! There was a problem submitting your form' });
        }
      }
    } catch (error) {
      setErrors({ submit: 'Oops! There was a problem submitting your form' });
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phonenumber: '',
      projectType: '',
      message: '',
      subscribe: false
    });
    setErrors({});
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line ri-2x text-green-500"></i>
              </div>
              <h3 className="text-xl font-semibold mb-2">Message Sent Successfully!</h3>
              <p className="mb-4">Thank you for reaching out. We'll get back to you within 24 hours.</p>
              <button 
                onClick={resetForm}
                className="px-6 py-2 bg-green-600 text-white font-medium rounded-button hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                Send Another Message
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Book a Business Needs Analysis</h2>
          <div className="w-20 h-1 bg-primary mx-auto mb-8"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-700">
            📈 Free Discovery Call to Improve Your Workflow. Let's identify your bottlenecks and design solutions that deliver results.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Your name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="your.email@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              
              <div>
                <label htmlFor="phonenumber" className="block text-gray-700 font-medium mb-2">Phone Number</label>
                <input 
                  type="text" 
                  id="phonenumber" 
                  name="phonenumber" 
                  value={formData.phonenumber}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20"
                  placeholder="+ 91"
                />
              </div>
              
              <div>
                <label htmlFor="projectType" className="block text-gray-700 font-medium mb-2">Project Type</label>
                <div className="relative">
                  <select 
                    id="projectType" 
                    name="projectType" 
                    value={formData.projectType}
                    onChange={handleChange}
                    className="w-full appearance-none px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 bg-white pr-8"
                  >
                    <option value="" disabled selected>Select your requirement</option>
                    <option value="erp-optimization">ERP Optimization / Custom ERP</option>
                    <option value="process-automation">Business Process Automation</option>
                    <option value="inventory-system">Inventory & Stock Management System</option>
                    <option value="procurement-system">Procurement & Purchase Workflow Automation</option>
                    <option value="reporting-analytics">Business Reporting & Analytics Dashboard</option>
                    <option value="digital-transformation">Complete Operational Digital Transformation</option>
                    <option value="ai-intelligence">AI-Based Business Intelligence</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </div>
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Message</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows="4" 
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Tell us about your project..."
                />
                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
              </div>
              
              <div className="flex items-center">
                <label className="custom-checkbox text-gray-700">
                  <input 
                    type="checkbox" 
                    name="subscribe" 
                    checked={formData.subscribe}
                    onChange={handleChange}
                  />
                  <span className="checkmark"></span>
                  Subscribe to our newsletter for startup tips and resources
                </label>
              </div>
              
              {errors.submit && <p className="text-red-500 text-sm">{errors.submit}</p>}
              
                <button 
                type="submit" 
                className="w-full px-6 py-3 bg-primary text-white font-medium !rounded-button hover:bg-primary/90 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
              >
                Book Free Discovery Call
              </button>
            </form>
          </div>
          
          <div className="bg-gray-50 p-8 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">What Happens Next?</h3>
            <div className="space-y-8">
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                  <span className="font-semibold text-primary">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Business Needs Analysis</h4>
                  <p className="text-gray-700">We analyze your current processes, identify bottlenecks, and understand your business goals.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                  <span className="font-semibold text-primary">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Custom Solution Design</h4>
                  <p className="text-gray-700">We design a tailored system that addresses your specific challenges with measurable outcomes.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                  <span className="font-semibold text-primary">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Implementation & Results</h4>
                  <p className="text-gray-700">We build and deploy your solution, tracking ROI and business impact from day one.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                  <span className="font-semibold text-primary">4</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Continuous Optimization</h4>
                  <p className="text-gray-700">We monitor performance and continuously improve your systems for sustained growth.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-4">Contact Information</h4>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center mr-3 text-primary">
                    <i className="ri-mail-line"></i>
                  </div>
                  <p className="text-gray-700">
                    <a href="mailto:contactus@krishnaapihub.com" className="hover:text-primary transition-colors">
                    contactus@krishnaapihub.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
